/**
 * Shell ports + PE/RB use cases (`modules/shared` does not import those modules directly).
 */
import type { AppContainer } from './AppContainer.ts';
import type { ShellAuthApplicationPort } from '@src/modules/shared/app-context/domain/ports/ShellAuthApplicationPort.ts';
import type { ShellLayoutPort } from '@src/modules/shared/app-context/domain/ports/ShellLayoutPort.ts';
import type { ShellNavigationPort } from '@src/modules/shared/app-context/domain/ports/ShellNavigationPort.ts';
import type { ShellUiMetadataPort } from '@src/modules/shared/app-context/domain/ports/ShellUiMetadataPort.ts';
import type { CoreShellPorts } from '@src/modules/shared/routing/domain/CoreShellPorts.ts';
import { PuntosEntregaApplicationService } from '@src/modules/puntos_entrega/application/PuntosEntregaApplicationService.ts';
import type { RedencionBonoApplicationService } from '@src/modules/redencion_bono/application/RedencionBonoApplicationService.ts';
import type { CatalogDownloadPort } from '@src/modules/sync/domain/ports/CatalogDownloadPort.ts';
import type { OfflineSyncPort } from '@src/modules/sync/domain/ports/OfflineSyncPort.ts';
import type { SyncStorageMetricsPort } from '@src/modules/sync/domain/ports/SyncStorageMetricsPort.ts';
import type { AppMaintenancePort } from '@src/modules/sync/domain/ports/AppMaintenancePort.ts';
import type { AppUpdatePort } from '@src/modules/sync/domain/ports/AppUpdatePort.ts';
import type { ObservabilidadPersistedReadPort } from '@src/modules/observabilidad/domain/ports/ObservabilidadPersistedReadPort.ts';

export type FeatureShellPorts = Omit<CoreShellPorts, 'puntosEntregaShell'> & {
  puntosEntregaShell: PuntosEntregaApplicationService;
  redencionBonoApplication: RedencionBonoApplicationService;
  syncStorageMetrics: SyncStorageMetricsPort;
  offlineSync: OfflineSyncPort;
  catalogDownload: CatalogDownloadPort;
  observabilidadPersistedRead: ObservabilidadPersistedReadPort;
  appMaintenance: AppMaintenancePort;
  appUpdate: AppUpdatePort;
};

/** Creates an object containing all the application ports required by the shell features. */
export function createFeatureShellPorts(opts: {
  container: AppContainer;
  auth: ShellAuthApplicationPort;
  navigation: ShellNavigationPort;
  layout: ShellLayoutPort;
  shellUi: ShellUiMetadataPort;
}): FeatureShellPorts {
  const puntos = opts.container.getPuntosEntregaShell();
  const offlineSync = opts.container.getOfflineSync();
  return {
    notifications: opts.container.getNotificationPort(),
    telemetry: opts.container.getTelemetry(),
    navigation: opts.navigation,
    layout: opts.layout,
    auth: opts.auth,
    shellUi: opts.shellUi,
    pendingSyncBadge: {
      getPendingUploadRecordCount(): number {
        const { puntosEntrega, redenciones } = offlineSync.getExportRecordCounts();
        return puntosEntrega + redenciones;
      },
    },
    puntosEntregaShell: puntos,
    redencionBonoApplication: opts.container.getRedencionBonoApplicationService(),
    syncStorageMetrics: opts.container.getSyncStorageMetrics(),
    offlineSync,
    catalogDownload: opts.container.getCatalogDownload(),
    observabilidadPersistedRead: opts.container.getObservabilidadPersistedRead(),
    appMaintenance: opts.container.getAppMaintenance(),
    appUpdate: opts.container.getAppUpdate(),
  };
}
